import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'
import { useImpersonationStore } from '@/stores/impersonationStore'
import { useCallback } from 'react'

// =============================================================================
// TYPES
// =============================================================================

export interface ImpersonationResponse {
  session_id: string
  tenant_id: string
  tenant_name: string
  tenant_slug: string
  access_token: string
  expires_at: string
}

export interface ImpersonationState {
  is_impersonating: boolean
  session_id: string | null
  tenant_id: string | null
  tenant_name: string | null
  tenant_slug: string | null
  started_at: string | null
}

export interface ImpersonationSession {
  id: string
  employee_user_id: string
  employee_name: string
  employee_email: string
  target_tenant_id: string
  target_tenant_name: string
  target_tenant_slug: string
  started_at: string
  ended_at: string | null
  is_active: boolean
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  currentState: ['impersonation', 'current'],
  sessions: (activeOnly?: boolean) => ['impersonation', 'sessions', { activeOnly }],
}

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Get current impersonation state from server
 */
export function useImpersonationState() {
  return useQuery<ImpersonationState>({
    queryKey: QUERY_KEYS.currentState,
    queryFn: async () => {
      const res = await api.get('/admin/impersonate/current')
      return res.data
    },
    staleTime: 1000 * 30, // 30 seconds
  })
}

/**
 * Get list of impersonation sessions
 */
export function useImpersonationSessions(activeOnly = false, limit = 50) {
  return useQuery<ImpersonationSession[]>({
    queryKey: QUERY_KEYS.sessions(activeOnly),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (activeOnly) params.append('active_only', 'true')
      params.append('limit', String(limit))

      const res = await api.get(`/admin/impersonate/sessions?${params.toString()}`)
      return res.data
    },
  })
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Start impersonating a tenant
 */
export function useStartImpersonation() {
  const queryClient = useQueryClient()
  const { startImpersonation } = useImpersonationStore()

  return useMutation<ImpersonationResponse, Error, string>({
    mutationFn: async (tenantId: string) => {
      const res = await api.post(`/admin/impersonate/${tenantId}`)
      return res.data
    },
    onSuccess: (data) => {
      // Update local store
      startImpersonation({
        sessionId: data.session_id,
        tenantId: data.tenant_id,
        tenantName: data.tenant_name,
        tenantSlug: data.tenant_slug,
        accessToken: data.access_token,
      })

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentState })
      queryClient.invalidateQueries({ queryKey: ['impersonation', 'sessions'] })
    },
  })
}

/**
 * End current impersonation session
 */
export function useEndImpersonation() {
  const queryClient = useQueryClient()
  const { endImpersonation } = useImpersonationStore()

  return useMutation<{ message: string; session_id: string }, Error, void>({
    mutationFn: async () => {
      const res = await api.post('/admin/impersonate/exit')
      return res.data
    },
    onSuccess: () => {
      // Clear local store
      endImpersonation()

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentState })
      queryClient.invalidateQueries({ queryKey: ['impersonation', 'sessions'] })
    },
  })
}

// =============================================================================
// COMBINED HOOK
// =============================================================================

/**
 * Main impersonation hook - combines store state with mutations
 */
export function useImpersonation() {
  const store = useImpersonationStore()
  const startMutation = useStartImpersonation()
  const endMutation = useEndImpersonation()

  // Start impersonating a tenant
  const impersonate = useCallback(
    async (tenantId: string) => {
      await startMutation.mutateAsync(tenantId)
    },
    [startMutation]
  )

  // Exit impersonation mode
  const exitImpersonation = useCallback(async () => {
    await endMutation.mutateAsync()
  }, [endMutation])

  return {
    // State
    isImpersonating: store.isImpersonating,
    sessionId: store.sessionId,
    tenantId: store.tenantId,
    tenantName: store.tenantName,
    tenantSlug: store.tenantSlug,
    startedAt: store.startedAt,

    // Actions
    impersonate,
    exitImpersonation,

    // Loading states
    isStarting: startMutation.isPending,
    isEnding: endMutation.isPending,
    isLoading: startMutation.isPending || endMutation.isPending,

    // Errors
    startError: startMutation.error,
    endError: endMutation.error,
  }
}
